import { useContext, useState, useMemo, useRef, useEffect } from "react";
import { FiBell, FiChevronDown, FiLogOut, FiUser } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import Context from "../context/Context";
import useWebSocketNotifications from "./admin/hooks/ws/useWebSocketNotifications";
import { fetchNotifications, markAllAsReadNotifications, markAsReadSingleNotification } from "../api/admin/notificationApi";
import ReactNotifications from "./common/reactNotifications/ReactNotifications";
import * as utilConstants from "../utils/constants/utilsConstants";
import { CiLogout } from "react-icons/ci";
import { BsFillBellFill } from "react-icons/bs";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const navigate = useNavigate();
  const { user, setUser, pageTitle, isSidebarOpen } = useContext(Context);

  const token = Cookies.get("adminToken");
  const wsNotifications = useWebSocketNotifications(token);

  const [apiNotifications, setApiNotifications] = useState([]);
  const [readIds, setReadIds] = useState([]);

  const profileRef = useRef(null);
  const notifRef = useRef(null);

  useEffect(() => {
    fetchNotifications({}, (res) => {
      const apiData = res?.data ?? [];
      setApiNotifications(Array.isArray(apiData) ? apiData : []);
    });
  }, []);

  const notifications = useMemo(() => {
    return [...apiNotifications]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [apiNotifications]);

  const unreadNotifications = useMemo(() => {
    return notifications.filter((n) => !readIds.includes(n.id) && !n.is_read);
  }, [notifications, readIds]);

  useEffect(() => {
    if (wsNotifications.length > 0) {
      const latestNotif = wsNotifications[wsNotifications.length - 1];

      ReactNotifications(
        latestNotif.company_name,
        utilConstants.getInfoStatus,
        "New Notification"
      );

      fetchNotifications({}, (res) => {
        const apiData = res?.data ?? [];
        setApiNotifications(Array.isArray(apiData) ? apiData : []);
      });
    }
  }, [wsNotifications]);


  const markAllAsRead = () => {
    markAllAsReadNotifications((res) => {
      if (res) {
        setReadIds(notifications.map((n) => n.id));
        setNotifOpen(false);
      }
    });
  };

  const markSingleAsRead = (id) => {
    markAsReadSingleNotification(id, (res) => {
      if (res) {
        setReadIds((prev) => [...prev, id]);
      }
    });
  };

  const onLogout = () => {
    Cookies.remove("adminToken");
    setUser(null);
    navigate("/login");
  };

  const fullName = `${user?.first_name?.charAt(0).toUpperCase() + user?.first_name?.slice(1)} ${user?.last_name?.charAt(0).toUpperCase() + user?.last_name?.slice(1)}`;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header className="sticky top-0 z-30 flex items-center justify-between px-2 py-2 bg-white shadow-sm border-b header-body">
      <h2 className="text-2xl font-semibold truncate !mb-0" style={{ paddingLeft: isSidebarOpen ? '187px' : '13px', transition: 'padding-left 0.3s ease' }}>{pageTitle}</h2>

      <div className="flex items-center gap-3">

        {user?.role === "superuser" && (
          <div className="relative" ref={notifRef}>
            <button
              className="relative p-2 rounded-full hover:bg-gray-100"
              onClick={() => setNotifOpen(!notifOpen)}
            >
              <BsFillBellFill color="black" className="h-5 w-5 text-gray-600" />
              {unreadNotifications.length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center h-5 w-5 rounded-full bg-red-500 text-white text-xs font-medium">
                  {unreadNotifications.length}
                </span>
              )}
            </button>

            {notifOpen && (
              <div className="absolute right-0 mt-2 w-80 bg-white border rounded-lg shadow-lg py-2 z-50">
                <div className="flex items-center justify-between px-4 py-2 border-b items-baseline">
                  <p className="font-medium text-sm">Notifications</p>
                  {unreadNotifications.length > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Mark all as read
                    </button>
                  )}
                </div>
                <div className="max-h-[500px] overflow-y-auto">
                  {notifications.length > 0 ? (
                    notifications.map((n) => {
                      const isRead = readIds.includes(n.id) || n.is_read;
                      return (
                        <div
                          key={n.id}
                          className={`px-4 py-3 border-b last:border-0 text-sm transition-colors ${isRead ? "bg-gray-50 text-gray-400" : "bg-white text-gray-800"
                            }`}
                        >
                          <p className="font-medium">{n.notification_text}</p>
                          <p className="text-xs text-gray-500 capitalize">
                            {n.notification_type}
                          </p>
                          <p className="text-xs text-gray-400 mb-2">
                            {new Date(n.created_at).toLocaleString()}
                          </p>

                          {!isRead && (
                            <button
                              onClick={() => markSingleAsRead(n.id)}
                              className="text-xs px-3 py-1 rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 transition"
                            >
                              Mark as read
                            </button>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-center text-gray-500 py-4 text-sm">
                      No notifications
                    </p>
                  )}
                </div>
              </div>
            )}

          </div>
        )}

        <div className="relative" ref={profileRef}>
          <button
            onClick={() => setOpen(!open)}
            className="flex items-center gap-1.5 p-1 rounded-[5px] hover:bg-gray-100 transition-colors"
          >
            <div className="h-10 w-10 flex-shrink-0 rounded-full bg-[#E5E7EB] overflow-hidden">
              {user?.profile_image ? (
                <img src={user.profile_image} alt="Profile" className="h-full w-full object-cover" />
              ) : (
                <div className="h-full w-full flex items-center justify-center text-xs font-bold text-gray-600 bg-gray-200">
                  {`${user?.first_name?.[0] || ""}${user?.last_name?.[0] || ""}`.toUpperCase()}
                </div>
              )}
            </div>
            <FiChevronDown className="h-3.5 w-3.5 text-gray-700" />
          </button>

          {open && (
            <div className="absolute right-0 mt-2.5 w-[260px] bg-white shadow-lg rounded-[5px]  z-50 overflow-hidden">
              {/* Header Section: Compact Alignment */}
              <div className="flex items-center gap-2 px-4 py-3">
                <div className="h-10 w-10 flex-shrink-0 rounded-full bg-gray-200 overflow-hidden">
                  {user?.profile_image ? (
                    <img src={user.profile_image} alt="Profile" className="h-full w-full object-cover" />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-xs font-bold text-gray-500">
                      {`${user?.first_name?.[0] || ""}${user?.last_name?.[0] || ""}`.toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="flex flex-col min-w-0 leading-tight">
                  <h4 className="text-[16px] text-[#000000] !mb-0 !line-hieght-0 font-normal truncate">
                    {fullName}
                  </h4>
                  <p className="text-[12px] font-normal !mb-0 text-[#828282] truncate">
                    {user?.email}
                  </p>
                </div>
              </div>



              {/* Menu Options */}
              <div className="pb-1 ">
                <Link to="/profile" onClick={() => setOpen(false)} className="block no-underline">
                  <button className="w-full flex items-center gap-3 px-4 py-2.5 text-[16px] font-normal text-[#000000] hover:bg-gray-50 transition-colors text-left">
                    <FiUser className="h-5 w-5 text-[#000000]" />
                    <span>Profile</span>
                  </button>
                </Link>

                <div className="h-[1px] border-1 bg-[#E5E5E5] my-1 mx-2" />

                <button
                  onClick={onLogout}
                  className="w-full text-red-500 flex items-center gap-3 px-4 py-2.5 text-[16px] font-normal  transition-colors text-left"
                >
                  <CiLogout className="h-5 w-5 " />
                  <span>Log out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
